/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on May 22, 2026 by hoarfroster
 *
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

import { getStorageItem, setStorageItem } from '@utils/storage.ts';

/**
 * Available theme modes
 * - 'light': Light theme with bright backgrounds and dark text
 * - 'dark': Dark theme with dark backgrounds and light text
 */
export type ThemeMode = 'light' | 'dark';

/**
 * Theme color scheme configuration
 * Defines the color palette for different UI elements
 */
export interface ThemeColorScheme {
    /** Stable key used by theme persistence and selection logic */
    mode: ThemeMode;
    /** Human-readable name for the theme */
    name: string;
    /** Color definitions for various UI elements */
    colors: {
        /** Primary background color */
        bg: string;
        'content-bg-light': string;
        'content-bg': string;
        'content-bg-dark': string;
        /** Navigation bar background color */
        nav: string;
        /** Primary text color */
        text: string;
        'text-light': string;
        'text-muted': string;
        /** Active/selected state color */
        active: string;
        /** Text color used on active/accent surfaces */
        'active-contrast': string;
        /** Sidebar background color */
        sidebar: string;
        /** Accent line color */
        line: string;
        /** Avatar placeholder color */
        avatar: string;
        /** Input field background color */
        input: string;
        /** Input field border color */
        'input-border': string;
        /** Button background color */
        button: string;
        /** Text color used on primary button surfaces */
        'button-text': string;
        /** Button hover background color */
        'button-hover': string;
        /** Secondary button background */
        'button-secondary': string;
        /** Secondary button hover background */
        'button-secondary-hover': string;
        /** Border color for general use */
        border: string;
        /** Border color for dividers */
        'border-divider': string;
        /** Success color */
        success: string;
        /** Warning color */
        warning: string;
        /** Error color */
        error: string;
        /** Info color */
        info: string;
        /** Hover background for interactive elements */
        hover: string;
        /** Card background */
        'card-bg': string;
        /** Popup/modal background */
        'popup-bg': string;
        /** Icon color */
        icon: string;
        /** Link color */
        link: string;
        /** Focus ring color */
        focus: string;
        /** Disabled text color */
        'text-disabled': string;
        /** Badge background colors */
        'badge-blue': string;
        'badge-green': string;
        'badge-yellow': string;
        'badge-red': string;
        'badge-purple': string;
        /** Special background colors */
        'bg-blue-light': string;
        'bg-success-light': string;
        'bg-warning-light': string;
        'bg-error-light': string;
        /** Selection background */
        selection: string;
        /** Subtle background surface */
        'subtle-bg': string;
        /** Soft accent background */
        'accent-soft': string;
        /** Soft warning background */
        'warning-soft': string;
        /** Soft error background */
        'error-soft': string;
        /** Soft success background */
        'success-soft': string;
        /** Status pill backgrounds */
        'status-draft-bg': string;
        'status-draft-text': string;
        'status-existing-bg': string;
        'status-existing-text': string;
    };
}

/**
 * Complete theme configuration object
 */
export interface ThemeConfig {
    /** Current theme mode (light/dark) */
    mode: ThemeMode;
    /** Whether auto mode (system preference) is enabled */
    autoMode: boolean;
    /** Whether background image is enabled */
    backgroundEnabled: boolean;
}

// ============================================================================
// THEME COLOR CONFIGURATIONS
// ============================================================================

/**
 * Comprehensive theme color configurations
 * Uses the design system colors defined in index.css for consistency
 */
export const themeColorSchemes: Record<ThemeMode, ThemeColorScheme> = {
    light: {
        mode: 'light',
        name: 'Aqua Light',
        colors: {
            bg: 'rgb(244 247 251)',
            'content-bg-light': 'rgb(248 250 253)',
            'content-bg': 'rgb(255 255 255)',
            'content-bg-dark': 'rgb(235 240 247)',
            nav: 'rgb(255 255 255)',
            text: 'rgb(20 25 34)',
            'text-light': 'rgb(62 72 88)',
            'text-muted': 'rgb(112 124 143)',
            active: 'rgb(0 113 227)',
            'active-contrast': 'rgb(255 255 255)',
            sidebar: 'rgb(248 250 253)',
            line: 'rgb(208 218 231)',
            avatar: 'rgb(224 231 240)',
            input: 'rgb(255 255 255)',
            'input-border': 'rgb(205 216 231)',
            button: 'rgb(0 113 227)',
            'button-text': 'rgb(255 255 255)',
            'button-hover': 'rgb(0 92 184)',
            'button-secondary': 'rgb(240 244 250)',
            'button-secondary-hover': 'rgb(228 235 245)',
            border: 'rgb(210 220 233)',
            'border-divider': 'rgb(226 232 241)',
            success: 'rgb(25 135 84)',
            warning: 'rgb(181 111 0)',
            error: 'rgb(214 52 43)',
            info: 'rgb(0 113 227)',
            hover: 'rgb(239 244 251)',
            'card-bg': 'rgb(255 255 255)',
            'popup-bg': 'rgb(255 255 255)',
            icon: 'rgb(92 105 124)',
            link: 'rgb(0 113 227)',
            focus: 'rgb(0 113 227)',
            'text-disabled': 'rgb(161 172 188)',
            'badge-blue': 'rgb(0 113 227)',
            'badge-green': 'rgb(25 135 84)',
            'badge-yellow': 'rgb(201 139 0)',
            'badge-red': 'rgb(214 52 43)',
            'badge-purple': 'rgb(111 85 199)',
            'bg-blue-light': 'rgb(231 242 255)',
            'bg-success-light': 'rgb(227 244 235)',
            'bg-warning-light': 'rgb(255 246 224)',
            'bg-error-light': 'rgb(255 238 237)',
            selection: 'rgb(202 225 255)',
            'subtle-bg': 'rgb(248 250 253)',
            'accent-soft': 'rgb(231 242 255)',
            'warning-soft': 'rgb(255 238 199)',
            'error-soft': 'rgb(255 225 223)',
            'success-soft': 'rgb(217 240 227)',
            'status-draft-bg': 'rgb(255 247 214)',
            'status-draft-text': 'rgb(127 83 0)',
            'status-existing-bg': 'rgb(231 242 255)',
            'status-existing-text': 'rgb(0 79 158)',
        },
    },
    dark: {
        mode: 'dark',
        name: 'Midnight Blue',
        colors: {
            bg: 'rgb(16 19 25)',
            'content-bg-light': 'rgb(30 35 44)',
            'content-bg': 'rgb(24 28 36)',
            'content-bg-dark': 'rgb(40 47 59)',
            nav: 'rgb(20 24 31)',
            text: 'rgb(241 245 249)',
            'text-light': 'rgb(206 215 227)',
            'text-muted': 'rgb(146 159 178)',
            active: 'rgb(88 166 255)',
            'active-contrast': 'rgb(255 255 255)',
            sidebar: 'rgb(20 24 31)',
            line: 'rgb(60 70 86)',
            avatar: 'rgb(53 62 77)',
            input: 'rgb(30 35 44)',
            'input-border': 'rgb(62 73 91)',
            button: 'rgb(10 132 255)',
            'button-text': 'rgb(255 255 255)',
            'button-hover': 'rgb(64 156 255)',
            'button-secondary': 'rgb(34 40 50)',
            'button-secondary-hover': 'rgb(45 53 66)',
            border: 'rgb(62 73 91)',
            'border-divider': 'rgb(43 51 64)',
            success: 'rgb(74 222 128)',
            warning: 'rgb(245 190 92)',
            error: 'rgb(255 111 103)',
            info: 'rgb(88 166 255)',
            hover: 'rgb(36 43 54)',
            'card-bg': 'rgb(24 28 36)',
            'popup-bg': 'rgb(27 32 41)',
            icon: 'rgb(157 170 189)',
            link: 'rgb(88 166 255)',
            focus: 'rgb(88 166 255)',
            'text-disabled': 'rgb(93 105 124)',
            'badge-blue': 'rgb(88 166 255)',
            'badge-green': 'rgb(74 222 128)',
            'badge-yellow': 'rgb(245 190 92)',
            'badge-red': 'rgb(255 111 103)',
            'badge-purple': 'rgb(184 166 255)',
            'bg-blue-light': 'rgb(12 45 89)',
            'bg-success-light': 'rgb(19 78 50)',
            'bg-warning-light': 'rgb(92 62 12)',
            'bg-error-light': 'rgb(94 32 29)',
            selection: 'rgb(20 74 138)',
            'subtle-bg': 'rgb(30 35 44)',
            'accent-soft': 'rgb(12 45 89)',
            'warning-soft': 'rgb(79 52 10)',
            'error-soft': 'rgb(83 29 27)',
            'success-soft': 'rgb(18 68 45)',
            'status-draft-bg': 'rgb(79 52 10)',
            'status-draft-text': 'rgb(255 216 143)',
            'status-existing-bg': 'rgb(12 45 89)',
            'status-existing-text': 'rgb(156 203 255)',
        },
    },
};

// ============================================================================
// STORAGE KEYS
// ============================================================================

/**
 * localStorage keys for theme persistence
 */
const STORAGE_KEYS = {
    MODE: 'theme-mode',
    AUTO: 'theme-auto',
    BACKGROUND: 'theme-background',
} as const;

/**
 * Legacy storage keys kept for backward compatibility
 * These were used by early theme switcher versions and are read-only now.
 */
const LEGACY_STORAGE_KEYS = {
    MODE: 'hf-theme-mode',
    AUTO: 'hf-theme-auto',
    BACKGROUND: 'hf-theme-background',
} as const;

// ============================================================================
// CORE THEME FUNCTIONS
// ============================================================================

/**
 * Initializes the theme system on application startup
 *
 * This function:
 * 1. Reads saved theme preferences from localStorage
 * 2. Detects system color scheme preference if auto mode is enabled
 * 3. Applies the determined theme immediately
 * 4. Sets up system preference change listeners
 *
 * @returns {ThemeConfig} The current theme configuration
 *
 * @example
 * ```typescript
 * // Initialize theme system on app startup
 * const themeConfig = initializeTheme();
 * console.log(`Current theme: ${themeConfig.mode}`);
 * ```
 */
export function initializeTheme(): ThemeConfig {
    const autoValue = getStorageItem(STORAGE_KEYS.AUTO) ?? getStorageItem(LEGACY_STORAGE_KEYS.AUTO);
    const autoMode = autoValue === null ? true : autoValue === 'true';
    const storedMode = (getStorageItem(STORAGE_KEYS.MODE) ??
        getStorageItem(LEGACY_STORAGE_KEYS.MODE)) as ThemeMode | null;
    const backgroundEnabled =
        (getStorageItem(STORAGE_KEYS.BACKGROUND) ?? getStorageItem(LEGACY_STORAGE_KEYS.BACKGROUND)) !== 'false';
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

    // Determine current theme mode
    let currentMode: ThemeMode;
    if (autoMode) {
        currentMode = systemPrefersDark ? 'dark' : 'light';
    } else {
        currentMode = storedMode ?? (systemPrefersDark ? 'dark' : 'light');
    }

    // Apply theme immediately
    applyTheme(currentMode, backgroundEnabled);

    // Set up system preference listener
    setupSystemThemeListener();

    return {
        mode: currentMode,
        autoMode,
        backgroundEnabled,
    };
}

/**
 * Applies a theme mode to the application
 *
 * This function:
 * 1. Updates CSS custom properties for TailwindCSS integration
 * 2. Updates body classes for theme-specific styling
 * 3. Applies background image if enabled
 * 4. Persists settings to localStorage
 *
 * @param {ThemeMode} mode - The theme mode to apply
 * @param {boolean} [backgroundEnabled=true] - Whether to show background image
 *
 * @example
 * ```typescript
 * // Switch to dark theme with background
 * applyTheme('dark', true);
 *
 * // Switch to light theme without background
 * applyTheme('light', false);
 * ```
 */
export function applyTheme(mode: ThemeMode, backgroundEnabled = true): void {
    const scheme = themeColorSchemes[mode];
    const root = document.documentElement;
    const body = document.body;

    // Update CSS custom properties for TailwindCSS
    Object.entries(scheme.colors).forEach(([key, value]) => {
        root.style.setProperty(`--color-theme-${key}`, value);
    });

    // Update body classes
    body.classList.remove('light', 'dark');
    body.classList.add(mode);

    // Apply background image
    applyBackgroundImage(backgroundEnabled);

    // Update preview root if it exists (for component previews)
    const previewRoot = document.querySelector('.preview-root');
    if (previewRoot) {
        previewRoot.classList.remove('light', 'dark');
        previewRoot.classList.add(mode);
    }

    // Persist settings
    setStorageItem(STORAGE_KEYS.MODE, mode);
    setStorageItem(STORAGE_KEYS.BACKGROUND, backgroundEnabled.toString());
}

/**
 * Toggles between light and dark theme modes
 *
 * @param {boolean} [backgroundEnabled] - Optional background setting override
 * @returns {ThemeMode} The new theme mode after toggling
 *
 * @example
 * ```typescript
 * // Simple toggle
 * const newMode = toggleTheme();
 *
 * // Toggle with background disabled
 * const newMode = toggleTheme(false);
 * ```
 */
export function toggleTheme(backgroundEnabled?: boolean): ThemeMode {
    const currentConfig = getCurrentTheme();
    const newMode = currentConfig.mode === 'light' ? 'dark' : 'light';
    const bgEnabled = backgroundEnabled ?? currentConfig.backgroundEnabled;

    applyTheme(newMode, bgEnabled);
    return newMode;
}

/**
 * Enables or disables automatic theme switching based on system preference
 *
 * @param {boolean} enabled - Whether to enable auto mode
 *
 * @example
 * ```typescript
 * // Enable auto theme switching
 * setAutoMode(true);
 *
 * // Disable auto theme switching
 * setAutoMode(false);
 * ```
 */
export function setAutoMode(enabled: boolean): void {
    setStorageItem(STORAGE_KEYS.AUTO, enabled.toString());

    if (enabled) {
        const systemMode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        const currentConfig = getCurrentTheme();
        applyTheme(systemMode, currentConfig.backgroundEnabled);
    }
}

/**
 * Toggles the background image on/off
 *
 * @param {boolean} [enabled] - Optional explicit enable/disable, toggles if not provided
 * @returns {boolean} The new background enabled state
 *
 * @example
 * ```typescript
 * // Toggle background
 * const isEnabled = toggleBackground();
 *
 * // Explicitly enable background
 * toggleBackground(true);
 * ```
 */
export function toggleBackground(enabled?: boolean): boolean {
    const currentConfig = getCurrentTheme();
    const newEnabled = enabled ?? !currentConfig.backgroundEnabled;

    applyBackgroundImage(newEnabled);
    setStorageItem(STORAGE_KEYS.BACKGROUND, newEnabled.toString());

    return newEnabled;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Applies or removes the background image
 *
 * @param {boolean} enabled - Whether to show the background image
 * @private
 */
function applyBackgroundImage(enabled: boolean): void {
    const root = document.documentElement;
    const currentTheme = getCurrentTheme();

    if (enabled) {
        const backgroundImage =
            currentTheme.mode === 'dark' ? 'url(/website-background-dark.svg)' : 'url(/website-background.svg)';

        root.style.setProperty('--theme-bg-image', backgroundImage);
        root.style.setProperty('--theme-bg-size', 'cover');
        root.style.setProperty('--theme-bg-position', 'center');
        root.style.setProperty('--theme-bg-repeat', 'no-repeat');
        root.style.setProperty('--theme-bg-attachment', 'fixed');
    } else {
        root.style.removeProperty('--theme-bg-image');
        root.style.removeProperty('--theme-bg-size');
        root.style.removeProperty('--theme-bg-position');
        root.style.removeProperty('--theme-bg-repeat');
        root.style.removeProperty('--theme-bg-attachment');
    }
}

/**
 * Sets up a listener for system color scheme changes
 * Only applies changes if auto mode is enabled
 *
 * @returns {Function} Cleanup function to remove the listener
 * @private
 */
function setupSystemThemeListener(): () => void {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
        const autoMode = getStorageItem(STORAGE_KEYS.AUTO) === 'true';
        if (autoMode) {
            const newMode = mediaQuery.matches ? 'dark' : 'light';
            const currentConfig = getCurrentTheme();
            applyTheme(newMode, currentConfig.backgroundEnabled);
        }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
}

/**
 * Gets the current theme configuration
 *
 * @returns {ThemeConfig} Current theme configuration
 *
 * @example
 * ```typescript
 * const config = getCurrentTheme();
 * console.log(`Mode: ${config.mode}, Auto: ${config.autoMode}`);
 * ```
 */
export function getCurrentTheme(): ThemeConfig {
    const autoValue = getStorageItem(STORAGE_KEYS.AUTO) ?? getStorageItem(LEGACY_STORAGE_KEYS.AUTO);
    const autoMode = autoValue === null ? true : autoValue === 'true';
    const storedMode = (getStorageItem(STORAGE_KEYS.MODE) ??
        getStorageItem(LEGACY_STORAGE_KEYS.MODE)) as ThemeMode | null;
    const backgroundEnabled =
        (getStorageItem(STORAGE_KEYS.BACKGROUND) ?? getStorageItem(LEGACY_STORAGE_KEYS.BACKGROUND)) !== 'false';

    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    let currentMode: ThemeMode;
    if (autoMode) {
        currentMode = systemPrefersDark ? 'dark' : 'light';
    } else {
        currentMode = storedMode ?? (systemPrefersDark ? 'dark' : 'light');
    }

    return {
        mode: currentMode,
        autoMode,
        backgroundEnabled,
    };
}

/**
 * Gets the color scheme for a specific theme mode
 *
 * @param {ThemeMode} mode - The theme mode
 * @returns {ThemeColorScheme} The color scheme configuration
 *
 * @example
 * ```typescript
 * const lightColors = getThemeColors('light');
 * console.log(lightColors.colors.bg); // Light theme background color
 * ```
 */
export function getThemeColors(mode: ThemeMode): ThemeColorScheme {
    return themeColorSchemes[mode];
}
